'use client';

import { faArrowToBottom, faArrowToTop, faCircleQuestion, faCircleXmark, faEllipsis, faUniversalAccess } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Application } from '@pickleballinc/lib/types';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { isLegalPath } from '../../../lib/legal-paths';
import { HelpGuidesChatBot } from '../HelpGuidesChatBot';

import { UserWayIcon } from './icons/UserWayIcon';

declare global {
	interface Window {
		UserWay?: {
			widgetOpen?: () => void;
		};
	}
}

const APP_ACCENT_COLORS: Record<Application, string> = {
	pickleball: '#471CB1',
	tournament: '#114BBF',
	leagues: '#F73D68',
	'team-leagues': '#FF5005',
	clubs: '#B42318',
	'apr-rankings': '#4CA30D'
};

const SCROLL_ACTION_EXCLUDED_PATHS: Partial<Record<Application, (pathname: string) => boolean>> = {
	tournament: (pathname) => pathname.includes('/kiosk') || pathname.includes('/search'),
	clubs: (pathname) => !/^\/clubs\/[^/]+/.test(pathname)
};

interface FloatingMenuProps {
	app: Application;
	showScrollAction?: boolean;
	helpChatEnabled?: boolean;
	whiteListedPages: string[];
	/** Webview clients frame the legal pages with their own controls — see the early return below. */
	isWebview?: boolean;
}

const openAccessibilityMenu = () => {
	if (typeof window !== 'undefined' && window.UserWay?.widgetOpen) {
		window.UserWay.widgetOpen();
	}
};

const openHelpChat = () => {
	if (typeof window !== 'undefined' && window.HelpGuidesChat) {
		window.HelpGuidesChat('show', { expanded: true });
	}
};

const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 15000;

export function FloatingMenu({ app, showScrollAction = true, isWebview = false, helpChatEnabled = true, whiteListedPages }: FloatingMenuProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [availability, setAvailability] = useState({
		userway: false,
		helpGuides: false
	});
	const [isScrolled, setIsScrolled] = useState(false);

	const pathname = usePathname();
	const isPrivacyPage = pathname === '/privacy-terms-dmca';
	const isWatchNowPage = pathname === '/watch-now';

	const isBottomMode = isPrivacyPage && !isScrolled;
	const isScrollActionExcluded = SCROLL_ACTION_EXCLUDED_PATHS[app]?.(pathname) ?? false;

	const isScrollActionVisible = showScrollAction && !isWatchNowPage && !isScrollActionExcluded && (isPrivacyPage || isScrolled);

	const accentColor = APP_ACCENT_COLORS[app];

	const handleScroll = () => {
		setIsScrolled(window.scrollY > 300);
	};

	const handleScrollClick = () => {
		if (isBottomMode) {
			window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);
		handleScroll();
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		setAvailability({
			userway: false,
			helpGuides: false
		});

		const startedAt = Date.now();

		const checkAvailability = () => {
			const userwayReady = typeof window.UserWay?.widgetOpen === 'function';
			const helpGuidesReady = typeof window.HelpGuidesChat === 'function';

			setAvailability((prev) => {
				if (prev.userway === userwayReady && prev.helpGuides === helpGuidesReady) {
					return prev;
				}

				return {
					userway: userwayReady,
					helpGuides: helpGuidesReady
				};
			});

			const bothReady = userwayReady && helpGuidesReady;
			const timedOut = Date.now() - startedAt > POLL_TIMEOUT_MS;

			if (bothReady || timedOut) {
				clearInterval(intervalId);
			}
		};

		const intervalId = setInterval(checkAvailability, POLL_INTERVAL_MS);
		checkAvailability();

		return () => clearInterval(intervalId);
	}, [pathname]);

	// A legal page inside a webview: the native app supplies its own chrome, so the menu and the
	// scroll button below it are both duplicates. Decided here rather than in the root layout, which
	// does not re-render on a client-side navigation — this component does, on every pathname change.
	// Placed after every hook so the hook order stays identical on both branches.
	if (isWebview && isLegalPath(pathname)) return null;

	const baseMenuItems = [
		{
			id: 'accessibility',
			title: 'Accessibility',
			icon: faUniversalAccess,
			action: openAccessibilityMenu,
			requires: 'userway' as const
		},
		{
			id: 'help',
			title: 'Help',
			icon: faCircleQuestion,
			action: openHelpChat,
			requires: 'helpGuides' as const
		}
	];

	const menuItems = baseMenuItems.filter((item) => {
		if (item.requires === 'userway') return availability.userway;
		if (item.requires === 'helpGuides') return helpChatEnabled && availability.helpGuides;
		return true;
	});

	const hasMenu = menuItems.length > 0;

	return (
		<>
			{hasMenu ? (
				<div className="floating-menu fixed bottom-[264px] sm:bottom-[164px] right-3 z-[100] sm:right-4">
					<div className="flex flex-col items-center rounded-[24px] bg-white/30 p-2 backdrop-blur-md">
						<div
							className={clsx(
								'flex flex-col items-center gap-2 transition-all duration-200 ease-out',
								isOpen ? 'pointer-events-auto mb-2 max-h-96 opacity-100' : 'pointer-events-none max-h-0 opacity-0'
							)}
							aria-hidden={!isOpen}
						>
							{menuItems.map((item) => (
								<button
									key={item.id}
									type="button"
									aria-label={item.title}
									onClick={() => {
										setIsOpen(false);
										item.action();
									}}
									tabIndex={isOpen ? 0 : -1}
									style={{ backgroundColor: accentColor }}
									className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105 hover:brightness-90"
								>
									{item.id === 'accessibility' ? (
										<UserWayIcon className="h-10 w-10" />
									) : (
										<FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
									)}
								</button>
							))}
						</div>

						<button
							type="button"
							aria-label={isOpen ? 'Close utility menu' : 'Open utility menu'}
							aria-expanded={isOpen}
							onClick={() => setIsOpen((value) => !value)}
							style={{ backgroundColor: accentColor }}
							className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-xl transition-transform hover:scale-105 hover:brightness-90"
						>
							<FontAwesomeIcon icon={isOpen ? faCircleXmark : faEllipsis} className="h-5 w-5" />
						</button>
					</div>
				</div>
			) : null}

			<HelpGuidesChatBot enabled={helpChatEnabled} whiteListedPages={whiteListedPages} />

			{isScrollActionVisible ? (
				<button
					type="button"
					aria-label={isBottomMode ? 'Go to Bottom' : 'Back to Top'}
					onClick={handleScrollClick}
					style={{ backgroundColor: accentColor }}
					className={clsx(
						'back-to-top-button flex fixed bottom-[216px] sm:bottom-[116px] right-5 z-[100] h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-xl transition-transform hover:scale-105 hover:brightness-90 sm:right-6'
					)}
				>
					<FontAwesomeIcon icon={isBottomMode ? faArrowToBottom : faArrowToTop} className="h-5 w-5" />
				</button>
			) : null}
		</>
	);
}
